import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { EMPTY, SESSION_AUTH_TOKEN } from "../Constants/CommonConstants";
import {
  API_INITIAL_STATE,
  BUILDER_TYPE_FULFILLED,
  BUILDER_TYPE_PENDING,
  BUILDER_TYPE_REJECTED,
  SLICE_AUTHORIZATION,
  SLICE_LOGIN,
  THUNK_GET_PERFORM_LOGOUT,
  THUNK_POST_REFRESH_JWT_TOKEN,
} from "../Constants/SliceAndThunkConstants";
import {
  IS_ACCOUNT_BLOCKED,
  SEND_OTP_DESCRIPTION,
} from "../Constants/VerifyAccountConstants";
import {
  generateJwtTokenService,
  getRefreshJwtTokenService,
  performLogoutService,
} from "../Services/AuthorizationService";
import {
  apiSliceBuilderService,
  clearApplicationData,
  parseJwt,
} from "../Services/CommonServices";

export const generateToken = createAsyncThunk(
  `${SLICE_LOGIN}/${THUNK_POST_REFRESH_JWT_TOKEN}`,
  async (_, { getState }) => {
    if (sessionStorage.getItem(SESSION_AUTH_TOKEN)) {
      return await getRefreshJwtTokenService();
    }
    if (getState().login.loginToken) {
      return await generateJwtTokenService();
    }
    return EMPTY;
  }
);
export const performLogout = createAsyncThunk(
  `${SLICE_LOGIN}/${THUNK_GET_PERFORM_LOGOUT}`,
  async () => {
    return await performLogoutService();
  }
);

const initialState = {
  jwtTokenValues: {
    jwt: "",
    iat: null,
    sub: null,
    exp: null,
    scopes: "",
    firstName: "",
    lastName: "",
    userName: "",
    isDevtoolsEnabled: false,
    hapId: "",
    isMailRead: "",
    timeShifterEnabledForUser: false,
    shiftedDateMillisFromEpoch: null,
    appNum: null,
    applicationDate: null,
    appId: null,
    entitlementList: [],
    isEntitledStaff: false,
  },
  generateTokenResponse: API_INITIAL_STATE,
  performLogoutResponse: API_INITIAL_STATE,
};
export const authorization = createSlice({
  name: SLICE_AUTHORIZATION,
  initialState,
  reducers: {
    setFirstNameLastName: (state, action) => {
      state.jwtTokenValues.firstName = action.payload.firstName;
      state.jwtTokenValues.lastName = action.payload.lastName;
    },
    setIsEntitledStaff: (state, action) => {
      state.jwtTokenValues.isEntitledStaff = action?.payload;
    },
    clearAuthorizationApiResponse: (state, action) => {
      const responseName = `${action.payload}Response`;
      if (!initialState[responseName]) {
        return;
      }
      state[responseName] = initialState[responseName];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(generateToken.pending, (state) => {
      state.generateTokenResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(generateToken.rejected, (state, action) => {
      state.generateTokenResponse = apiSliceBuilderService(
        BUILDER_TYPE_REJECTED,
        action
      );
    });
    builder.addCase(generateToken.fulfilled, (state, action) => {
      state.generateTokenResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );

      const payload = action.payload;
      const encodedToken = payload?.data?.token;
      if (encodedToken) {
        const decodedToken = parseJwt(encodedToken);
        if (decodedToken !== null) {
          state.jwtTokenValues = {
            jwt: encodedToken,
            iat: decodedToken.iat,
            sub: decodedToken.sub,
            exp: decodedToken.exp,
            scopes: decodedToken.scopes,
            isDevtoolsEnabled: decodedToken?.devtoolsEnabled,
            isMailRead: decodedToken?.isMailRead,
            firstName: decodedToken?.firstName,
            lastName: decodedToken?.lastName,
            shiftedDateMillisFromEpoch:
              decodedToken?.shiftedDateMillisFromEpoch,
            timeShifterEnabledForUser: decodedToken?.timeShifterEnabledForUser,
            appNum: decodedToken?.appNum,
            hapId: decodedToken?.hapId,
            applicationDate: decodedToken?.applicationDate,
            appId: decodedToken?.appId,
            userName: decodedToken?.userName,
            entitlementList: decodedToken?.entitlementList,
            isEntitledStaff: decodedToken?.isEntitledStaff,
          };
          sessionStorage.setItem(SESSION_AUTH_TOKEN, encodedToken);
          document.cookie = `FPACTJWTCookie=${encodedToken};samesite=strict;path=/;secure`;
        }
      } else {
        clearApplicationData();
        state.jwtTokenValues = initialState.jwtTokenValues;
      }
    });
    builder.addCase(performLogout.pending, (state) => {
      state.performLogoutResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(performLogout.rejected, (state, action) => {
      state.performLogoutResponse = apiSliceBuilderService(
        BUILDER_TYPE_REJECTED,
        action
      );
    });
    builder.addCase(performLogout.fulfilled, (state, action) => {
      state.performLogoutResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
      clearApplicationData();
      state.jwtTokenValues = initialState.jwtTokenValues;
      state.generateTokenResponse = API_INITIAL_STATE;
      sessionStorage.removeItem(SEND_OTP_DESCRIPTION);
      sessionStorage.removeItem(IS_ACCOUNT_BLOCKED);
    });
  },
});
export const {
  clearAuthorizationApiResponse,
  setFirstNameLastName,
  setIsEntitledStaff,
} = authorization.actions;
export default authorization.reducer;
